import { CircleHelp, Workflow } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, PointerEvent as ReactPointerEvent, RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
  Separator,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@shared/components/ui';
import { cn } from '@shared/components/ui/cn';

import { levelHint, levelName } from '../data/level-text';
import {
  barsOf,
  capOf,
  drawnStops,
  type Level,
  type Stop,
  type ThinkingChoice,
} from '../data/thinking-levels';
import type { ModelControls } from '../hooks/use-model-controls';

import { LevelGlyph } from './LevelGlyph';
import {
  boxAnchor,
  GAP,
  GUTTER,
  panelSide,
  viewportHeight,
  type PanelSide,
} from './popover-placement';

/** How far the first and last stops sit in from the track's ends: the thumb's radius (F5). */
const STOP_INSET = 8;

interface ThinkingPickerProps {
  controls: ModelControls;
  /** The slider moved: the chat's level. */
  onChoiceChange: (choice: ThinkingChoice) => void;
  agentic: boolean;
  /** Absent: the popover has no Agentic row. */
  onAgenticChange?: (on: boolean) => void;
  /** A connected session: the Agentic help reads `picker.agenticHintSteps`. */
  connected?: boolean;
  disabled?: boolean;
  /** The composer is narrower than CHIP_LABEL_MIN_WIDTH: the level name may truncate. */
  compact?: boolean;
  /** The composer box: the popover's anchor. */
  boxRef: RefObject<HTMLElement | null>;
  /** The composer clears its note when this popover closes. */
  onOpenChange?: (open: boolean) => void;
  /** The composer's note: a press inside it does not close this popover. */
  noteRef?: RefObject<HTMLElement | null>;
}

/**
 * The composer's Thinking chip ([level glyph][level][Agentic glyph]) and its popover (DEV-4575
 * Option C): the shown model's cap, the level dial and the Agentic switch.
 *
 * The dial's stops are the levels the shown model's family offers (`GET /api/v1/models`), then
 * the scale's steps above its cap, drawn hatched: they are not values and take no pointer or
 * key. Every opening reads `/models` again, the dial pending until that read answers. A move
 * sets the chat's level at once (`onChoiceChange`). Against an older orchestrator (no
 * `thinking_scale`) the dial offers Low, Medium and High, and while Agentic is on it is held:
 * that orchestrator runs Agentic at full depth.
 *
 * The popover anchors to the composer box's inline end, below it when its content fits there,
 * else above it, measured as it opens and on a resize.
 */
export function ThinkingPicker({
  controls,
  onChoiceChange,
  agentic,
  onAgenticChange,
  connected,
  disabled = false,
  compact = false,
  boxRef,
  onOpenChange,
  noteRef,
}: ThinkingPickerProps) {
  const { t, i18n } = useTranslation('ai-cio');
  const rtl = i18n.dir() === 'rtl';
  const { models, checking, refresh, unavailable, shown, offer, level } = controls;
  const [open, setOpen] = useState(false);
  /** The offered stop under the pointer: the hint line shows its hint. */
  const [hovered, setHovered] = useState<number | null>(null);
  const [side, setSide] = useState<PanelSide>('bottom');
  const chipRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const anchor = useMemo(() => ({ current: boxAnchor(boxRef, chipRef) }), [boxRef]);

  const uid = useId();
  const thinkingId = `${uid}-thinking`;
  const hintId = `${uid}-hint`;
  const agenticId = `${uid}-agentic`;
  const agenticDescriptionId = `${uid}-agentic-description`;

  const levels = offer?.levels ?? [];
  /** The offered stops, the dial's values. */
  const n = levels.length;
  const stops: Stop[] = offer ? drawnStops(models?.thinking_scale, offer) : [];
  const total = stops.length;
  const chosenIndex = level ? levels.indexOf(level) : -1;
  const cap = offer ? capOf(offer) : null;
  /** The shown model takes no level: no dial, and no level on the chip. */
  const noLevels = offer !== null && n === 0;
  /** An older orchestrator with Agentic on runs at full depth, so the dial is held. */
  const fullDepth = offer?.older === true && agentic;
  const pending = offer === null || checking;
  const nameOf = (l: Level) => levelName(t, l.id, l.label);
  const levelText = level ? nameOf(level) : t('picker.thinking', 'Thinking');
  // The name says what the chip draws: the level, and Agentic while it is on (its glyph is
  // hidden from screen readers).
  const chipName = level
    ? agentic
      ? t('picker.thinkingChipAgentic', 'Thinking: {{level}}, Agentic on', { level: levelText })
      : t('picker.thinkingChip', 'Thinking: {{level}}', { level: levelText })
    : agentic
      ? t('picker.thinkingAgentic', 'Thinking, Agentic on')
      : t('picker.thinking', 'Thinking');

  /**
   * Picks the side the popover opens on from its content's own height (its cap lifted to
   * measure it) and the room the viewport has above and below the composer box.
   */
  const decideSide = useCallback(() => {
    const content = contentRef.current;
    if (!content) return;
    const r = anchor.current.getBoundingClientRect();
    const maxHeight = content.style.maxHeight;
    content.style.maxHeight = 'none';
    const height = content.offsetHeight;
    content.style.maxHeight = maxHeight;
    setSide(panelSide(height, r.top - GAP - GUTTER, viewportHeight() - r.bottom - GAP - GUTTER));
  }, [anchor]);

  // While open, a resize decides the side again.
  useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', decideSide);
    return () => {
      window.removeEventListener('resize', decideSide);
    };
  }, [open, decideSide]);

  // Focus that waited on the popover for the pending dial goes to the slider.
  useEffect(() => {
    if (open && !pending && document.activeElement === contentRef.current) {
      sliderRef.current?.focus();
    }
  }, [open, pending]);

  function handleOpenChange(next: boolean) {
    if (next) {
      setHovered(null);
      refresh();
    }
    setOpen(next);
    onOpenChange?.(next);
  }

  function chooseLevel(index: number) {
    if (fullDepth || index === chosenIndex) return;
    const chosen = levels[index];
    if (chosen) onChoiceChange({ id: chosen.id, rank: chosen.rank });
  }

  /** The stop nearest a pointer; null over a hatched one. */
  function stopAt(clientX: number): number | null {
    const el = sliderRef.current;
    if (!el || total === 0) return null;
    const r = el.getBoundingClientRect();
    if (r.width === 0) return null;
    const from = rtl ? r.right - clientX : clientX - r.left;
    const pitch = total > 1 ? (r.width - 2 * STOP_INSET) / (total - 1) : 0;
    const nearest = pitch > 0 ? Math.round((from - STOP_INSET) / pitch) : 0;
    const at = Math.min(total - 1, Math.max(0, nearest));
    return at < n ? at : null;
  }

  function handleSliderPointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    if (fullDepth) return;
    const at = stopAt(e.clientX);
    if (at === null) return;
    dragging.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    chooseLevel(at);
  }

  function handleSliderPointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    const at = stopAt(e.clientX);
    // A drag past the cap holds at it.
    if (dragging.current) chooseLevel(at ?? n - 1);
    else setHovered(at);
  }

  function endDrag(e: ReactPointerEvent<HTMLDivElement>) {
    dragging.current = false;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }

  function handleSliderKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (fullDepth || n === 0) return;
    // The dial runs from the inline start, so in a right-to-left locale Left moves it up.
    const up = rtl ? 'ArrowLeft' : 'ArrowRight';
    const down = rtl ? 'ArrowRight' : 'ArrowLeft';
    let next: number;
    if (e.key === up || e.key === 'ArrowUp' || e.key === 'PageUp') {
      next = Math.min(chosenIndex + 1, n - 1);
    } else if (e.key === down || e.key === 'ArrowDown' || e.key === 'PageDown') {
      next = Math.max(chosenIndex - 1, 0);
    } else if (e.key === 'Home') {
      next = 0;
    } else if (e.key === 'End') {
      next = n - 1;
    } else {
      return;
    }
    e.preventDefault();
    chooseLevel(next);
  }

  // The track runs the dial's full width; the stops sit evenly along it, the first and last
  // STOP_INSET in from its ends. Each label has the stretch of track nearest its stop: the end
  // ones line up with the track's ends, the others centre on their stops (F5).
  const inset = `${String(STOP_INSET)}px`;
  const pitch = `(100% - 2 * ${inset}) / ${String(Math.max(total - 1, 1))}`;
  const single = total === 1;
  const at = (i: number) => (single ? '50%' : `calc(${inset} + ${pitch} * ${String(i)})`);
  const isEnd = (i: number) => !single && (i === 0 || i === total - 1);
  const slots = single
    ? '1fr'
    : stops
        .map((_, i) => (isEnd(i) ? `calc(${inset} + ${pitch} / 2)` : `calc(${pitch})`))
        .join(' ');
  const align = (i: number) => (!isEnd(i) ? 'text-center' : i === 0 ? 'text-start' : 'text-end');
  const hintLevel = hovered !== null ? levels[hovered] : level;
  const hintText = fullDepth
    ? t('level.fullDepth', 'On this server, Agentic runs at full depth')
    : offer && hintLevel
      ? levelHint(t, hintLevel, offer)
      : '';

  function stopLabel(stop: Stop, i: number) {
    const name = nameOf(stop.level);
    // The slider is the control; a label is a pointer shortcut to its stop.
    if (!stop.offered) {
      return (
        <span
          key={`${String(i)}-${stop.level.id}`}
          aria-hidden="true"
          title={name}
          data-hatched=""
          className={cn(
            'line-clamp-2 min-w-0 text-muted-foreground/50 [overflow-wrap:anywhere]',
            align(i),
          )}
        >
          {name}
        </span>
      );
    }
    return (
      <span
        key={`${String(i)}-${stop.level.id}`}
        aria-hidden="true"
        title={name}
        onClick={() => {
          chooseLevel(i);
        }}
        onPointerEnter={() => {
          setHovered(i);
        }}
        onPointerLeave={() => {
          setHovered(null);
        }}
        className={cn(
          'line-clamp-2 min-w-0 [overflow-wrap:anywhere]',
          align(i),
          fullDepth ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
          i === chosenIndex ? 'font-semibold text-foreground' : 'text-muted-foreground',
        )}
      >
        {name}
      </span>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          ref={chipRef}
          type="button"
          // While the chat's model is unavailable it names the chat's level and is inert: its
          // stops are the model's, which the list no longer gives. Drawn without the fade.
          disabled={disabled || unavailable !== null}
          aria-label={chipName}
          title={chipName}
          className={cn(
            'flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md text-sm text-foreground outline-none transition-colors hover:bg-accent focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed data-[state=open]:bg-accent',
            !unavailable && 'disabled:opacity-50',
            // As the Model chip: the drawn 10 px padding, 6 px in a narrow composer.
            compact ? 'min-w-0 px-1.5' : 'shrink-0 px-2.5',
          )}
        >
          {models === null ? (
            <span aria-hidden="true" className="h-5 w-12 rounded-md border bg-background" />
          ) : (
            <>
              <LevelGlyph filled={level ? barsOf(level.rank) : 0} tone="chip" />
              <span className={cn(compact && 'min-w-0 truncate')}>{levelText}</span>
              {agentic && <Workflow aria-hidden className="size-4 shrink-0" />}
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverAnchor virtualRef={anchor} />
      <PopoverContent
        ref={contentRef}
        side={side}
        align="end"
        sideOffset={GAP}
        collisionPadding={GUTTER}
        updatePositionStrategy="always"
        aria-label={t('picker.thinking', 'Thinking')}
        tabIndex={-1}
        // Portalled outside the AI CIO panel, it is still its surface (see ModelPicker).
        data-ai-cio-surface
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          decideSide();
          (sliderRef.current ?? contentRef.current)?.focus();
        }}
        onEscapeKeyDown={(e) => {
          // Escape closes the popover only: it never reaches the app's Escape that closes the
          // AI CIO panel.
          e.stopPropagation();
        }}
        onInteractOutside={(e) => {
          // The composer's note stays usable under the open popover: its Undo reaches the chat.
          if (e.target instanceof Node && noteRef?.current?.contains(e.target)) e.preventDefault();
        }}
        className="max-h-[var(--radix-popover-content-available-height)] w-80 max-w-[calc(100vw-2rem)] overflow-y-auto rounded-lg p-4 text-start"
      >
        <div className="flex items-baseline justify-between gap-3">
          <span id={thinkingId} className="shrink-0 text-sm font-medium text-foreground">
            {t('picker.thinking', 'Thinking')}
          </span>
          {shown && cap && (
            <span className="min-w-0 truncate text-xs text-muted-foreground">
              {t('picker.modelCap', '{{model}} · up to {{level}}', {
                model: shown.label,
                level: nameOf(cap),
              })}
            </span>
          )}
        </div>
        {pending ? (
          // The dial's block, held while the levels are read: its track, its stop labels and
          // the hint line, where the status shows.
          <>
            <div aria-hidden="true" className="relative mt-3 h-6">
              <span
                className="absolute top-1/2 h-1.5 -translate-y-1/2 animate-pulse rounded-full bg-secondary"
                style={{ insetInlineStart: '10%', insetInlineEnd: '10%' }}
              />
            </div>
            <div aria-hidden="true" className="mt-1.5 flex h-4 items-center justify-around">
              <span className="h-2 w-10 animate-pulse rounded-full bg-secondary" />
              <span className="h-2 w-10 animate-pulse rounded-full bg-secondary" />
              <span className="h-2 w-10 animate-pulse rounded-full bg-secondary" />
            </div>
            <p role="status" className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
              <span
                aria-hidden="true"
                className="inline-block size-3 animate-spin rounded-full border border-primary border-t-transparent"
              />
              {t('picker.checking', 'Checking this model’s levels…')}
            </p>
          </>
        ) : noLevels ? (
          <p className="mt-2 text-xs text-muted-foreground">
            {t('picker.noLevels', 'This model has no thinking levels')}
          </p>
        ) : (
          <>
            <div
              ref={sliderRef}
              role="slider"
              tabIndex={0}
              aria-labelledby={thinkingId}
              aria-valuemin={0}
              aria-valuemax={n - 1}
              aria-valuenow={chosenIndex}
              aria-valuetext={levelText}
              aria-describedby={hintId}
              aria-disabled={fullDepth || undefined}
              onKeyDown={handleSliderKeyDown}
              onPointerDown={handleSliderPointerDown}
              onPointerMove={handleSliderPointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onPointerLeave={() => {
                setHovered(null);
              }}
              className={cn(
                'group relative mt-3 h-6 touch-none select-none outline-none',
                fullDepth ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
              )}
            >
              <span
                aria-hidden="true"
                className="absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-secondary"
              />
              {total > n && (
                // The scale's steps above the cap: drawn, never values.
                <span
                  aria-hidden="true"
                  className="absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-[repeating-linear-gradient(-45deg,currentColor_0_1px,transparent_1px_3px)] text-muted-foreground/50"
                  style={{ insetInlineStart: at(n - 1), insetInlineEnd: 0 }}
                />
              )}
              <span
                aria-hidden="true"
                className="absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-foreground"
                style={{ insetInlineStart: 0, width: at(Math.max(chosenIndex, 0)) }}
              />
              {stops.map((stop, i) =>
                // A dot marks every stop but the chosen one, under the thumb; a hatched one's is
                // as faint as its hatching.
                i === chosenIndex ? null : (
                  <span
                    key={`${String(i)}-${stop.level.id}`}
                    aria-hidden="true"
                    className={cn(
                      'absolute top-1/2 size-1 -translate-x-1/2 -translate-y-1/2 rounded-full rtl:translate-x-1/2',
                      stop.offered ? 'bg-muted-foreground' : 'bg-muted-foreground/50',
                    )}
                    style={{ insetInlineStart: at(i) }}
                  />
                ),
              )}
              <span
                aria-hidden="true"
                className="absolute top-1/2 size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border border-foreground bg-background shadow-sm group-focus-visible:ring-2 group-focus-visible:ring-ring group-focus-visible:ring-offset-2 rtl:translate-x-1/2"
                style={{ insetInlineStart: at(Math.max(chosenIndex, 0)) }}
              />
            </div>
            <div className="mt-1.5 grid text-xs" style={{ gridTemplateColumns: slots }}>
              {stops.map(stopLabel)}
            </div>
            <p id={hintId} className="mt-2 text-sm text-muted-foreground">
              {hintText}
            </p>
          </>
        )}
        {onAgenticChange && (
          <>
            <Separator className="my-3" />
            <div className="flex items-start gap-3">
              <Workflow aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span id={agenticId} className="text-sm font-medium text-foreground">
                    {t('picker.agentic', 'Agentic')}
                  </span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label={t('picker.agenticHelp', 'What Agentic does')}
                          className="flex size-5 shrink-0 items-center justify-center rounded-full text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring"
                        >
                          <CircleHelp aria-hidden className="size-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-64">
                        {connected
                          ? t(
                              'picker.agenticHintSteps',
                              'Plans and runs many steps. Slower, and costs more.',
                            )
                          : t(
                              'picker.agenticHint',
                              'Plans and runs many steps, and can split the work across analysts. Slower, and costs more.',
                            )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <p id={agenticDescriptionId} className="mt-0.5 text-sm text-muted-foreground">
                  {t('picker.agenticDescription', 'Multi-step work. Slower, more tokens.')}
                </p>
              </div>
              <Switch
                checked={agentic}
                onCheckedChange={onAgenticChange}
                aria-labelledby={agenticId}
                aria-describedby={agenticDescriptionId}
                className="mt-0.5"
              />
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
